
import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Button from '../../components/base/Button';
import Input from '../../components/base/Input';
import Select from '../../components/base/Select';
import Modal from '../../components/base/Modal';

interface Investment {
  id: string;
  name: string;
  description: string;
  category: string;
  purchaseDate: string;
  purchasePrice: number;
  usefulLife: number; // Jahre
  depreciationMethod: 'linear' | 'degressive' | 'sofortabschreibung';
  currentValue: number;
  annualDepreciation: number;
  totalDepreciation: number;
  remainingValue: number;
  supplier: string;
  invoiceNumber: string;
  location: string;
  status: 'aktiv' | 'vollständig-abgeschrieben' | 'verkauft' | 'entsorgt';
  notes: string;
  automatId?: string;
  automatName?: string;
  roi?: number; // Return on Investment
  paybackPeriod?: number; // Amortisationsdauer in Monaten
  monthlyRevenue?: number; // Monatliche Einnahmen
}

interface DepreciationEntry {
  year: number;
  startValue: number;
  depreciation: number;
  endValue: number;
}

const categories = [
  'Automaten',
  'Fahrzeuge',
  'Büroausstattung',
  'IT-Equipment',
  'Maschinen',
  'Gebäude & Anlagen',
  'Software',
  'Wartung & Service',
  'Marketing',
  'Sonstiges'
];

const depreciationMethods = [
  { value: 'linear', label: 'Linear' },
  { value: 'degressive', label: 'Degressiv' },
  { value: 'sofortabschreibung', label: 'Sofortabschreibung' }
];

const statuses = [
  { value: 'aktiv', label: 'Aktiv', color: 'bg-green-100 text-green-800' },
  { value: 'vollständig-abgeschrieben', label: 'Vollständig abgeschrieben', color: 'bg-gray-100 text-gray-800' },
  { value: 'verkauft', label: 'Verkauft', color: 'bg-blue-100 text-blue-800' },
  { value: 'entsorgt', label: 'Entsorgt', color: 'bg-red-100 text-red-800' }
];

export default function Investitionen() {
  const { user } = useAuth();
  const [investments, setInvestments] = useState<Investment[]>([]);
  const [automaten, setAutomaten] = useState<any[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedInvestment, setSelectedInvestment] = useState<Investment | null>(null);
  const [showDepreciationModal, setShowDepreciationModal] = useState(false);
  const [showROIModal, setShowROIModal] = useState(false);
  const [editingInvestment, setEditingInvestment] = useState<Investment | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    purchaseDate: new Date().toISOString().split('T')[0],
    purchasePrice: '',
    usefulLife: '5',
    depreciationMethod: 'linear' as const,
    supplier: '',
    invoiceNumber: '',
    location: '',
    automatId: '',
    notes: '',
    monthlyRevenue: ''
  });

  // Load investments and automaten from Supabase
  useEffect(() => {
    if (user) {
      loadData();
    }
  }, [user]);

  const loadData = async () => {
    try {
      // Load investments
      const { data: investmentsData, error: investmentsError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'investment')
        .order('created_at', { ascending: false });

      if (investmentsError) throw investmentsError;

      const investmentList = investmentsData.map(item => ({
        id: item.id,
        ...item.data
      }));

      setInvestments(investmentList);

      // Load automaten
      const { data: automatenData, error: automatenError } = await supabase
        .from('daten')
        .select('*')
        .eq('user_id', user?.id)
        .eq('data_type', 'automat');

      if (automatenError) throw automatenError;

      const automatenList = automatenData.map(item => ({
        id: item.id,
        name: item.data.name,
        location: item.data.location
      }));

      setAutomaten(automatenList);

    } catch (error) {
      console.error('Error loading investment data:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateROI = (investment: Investment): { roi: number; paybackPeriod: number } => {
    const monthlyRevenue = investment.monthlyRevenue || 0;
    const annualRevenue = monthlyRevenue * 12;
    const roi = investment.purchasePrice > 0 ? (annualRevenue / investment.purchasePrice) * 100 : 0;
    const paybackPeriod = monthlyRevenue > 0 ? investment.purchasePrice / monthlyRevenue : 0;
    
    return { roi, paybackPeriod };
  };

  const calculateDepreciation = (investment: Omit<Investment, 'id' | 'currentValue' | 'annualDepreciation' | 'totalDepreciation' | 'remainingValue' | 'roi' | 'paybackPeriod'>) => {
    const currentYear = new Date().getFullYear();
    const purchaseYear = new Date(investment.purchaseDate).getFullYear();
    const yearsElapsed = currentYear - purchaseYear;

    let annualDepreciation = 0;
    let totalDepreciation = 0;
    let currentValue = investment.purchasePrice;

    if (investment.depreciationMethod === 'sofortabschreibung') {
      annualDepreciation = investment.purchasePrice;
      totalDepreciation = investment.purchasePrice;
      currentValue = 0;
    } else if (investment.depreciationMethod === 'linear') {
      annualDepreciation = investment.purchasePrice / investment.usefulLife;
      totalDepreciation = Math.min(annualDepreciation * yearsElapsed, investment.purchasePrice);
      currentValue = investment.purchasePrice - totalDepreciation;
    } else if (investment.depreciationMethod === 'degressive') {
      const depreciationRate = 0.2;
      let remainingValue = investment.purchasePrice;
      
      for (let i = 0; i < yearsElapsed && remainingValue > 0; i++) {
        const yearlyDepreciation = remainingValue * depreciationRate;
        totalDepreciation += yearlyDepreciation;
        remainingValue -= yearlyDepreciation;
      }
      
      annualDepreciation = remainingValue > 0 ? remainingValue * depreciationRate : 0;
      currentValue = investment.purchasePrice - totalDepreciation;
    }

    const { roi, paybackPeriod } = calculateROI({
      ...investment,
      currentValue,
      annualDepreciation,
      totalDepreciation,
      remainingValue: Math.max(0, currentValue)
    } as Investment);

    return {
      currentValue: Math.max(0, currentValue),
      annualDepreciation,
      totalDepreciation,
      remainingValue: Math.max(0, currentValue),
      roi,
      paybackPeriod
    };
  };

  const generateDepreciationSchedule = (investment: Investment): DepreciationEntry[] => {
    const schedule: DepreciationEntry[] = [];
    const purchaseYear = new Date(investment.purchaseDate).getFullYear();
    
    if (investment.depreciationMethod === 'sofortabschreibung') {
      schedule.push({
        year: purchaseYear,
        startValue: investment.purchasePrice,
        depreciation: investment.purchasePrice,
        endValue: 0
      });
    } else if (investment.depreciationMethod === 'linear') {
      const annualDepreciation = investment.purchasePrice / investment.usefulLife;
      let currentValue = investment.purchasePrice;
      
      for (let i = 0; i < investment.usefulLife; i++) {
        const year = purchaseYear + i;
        const depreciation = Math.min(annualDepreciation, currentValue);
        
        schedule.push({
          year,
          startValue: currentValue,
          depreciation,
          endValue: currentValue - depreciation
        });
        
        currentValue -= depreciation;
      }
    } else if (investment.depreciationMethod === 'degressive') {
      const depreciationRate = 0.2;
      let currentValue = investment.purchasePrice;
      let year = purchaseYear;
      
      while (currentValue > 1 && year < purchaseYear + 20) {
        const depreciation = currentValue * depreciationRate;
        
        schedule.push({
          year,
          startValue: currentValue,
          depreciation,
          endValue: currentValue - depreciation
        });
        
        currentValue -= depreciation;
        year++;
      }
    }
    
    return schedule;
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      purchaseDate: new Date().toISOString().split('T')[0],
      purchasePrice: '',
      usefulLife: '5',
      depreciationMethod: 'linear',
      supplier: '',
      invoiceNumber: '',
      location: '',
      automatId: '',
      notes: '',
      monthlyRevenue: ''
    });
  };

  const handleEdit = (investment: Investment) => {
    setEditingInvestment(investment);
    setFormData({
      name: investment.name,
      description: investment.description,
      category: investment.category,
      purchaseDate: investment.purchaseDate,
      purchasePrice: investment.purchasePrice.toString(),
      usefulLife: investment.usefulLife.toString(),
      depreciationMethod: investment.depreciationMethod,
      supplier: investment.supplier,
      invoiceNumber: investment.invoiceNumber,
      location: investment.location,
      automatId: investment.automatId || '',
      notes: investment.notes,
      monthlyRevenue: investment.monthlyRevenue?.toString() || ''
    });
    setIsEditModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const selectedAutomat = automaten.find(a => a.id === formData.automatId);
    const baseInvestment = {
      name: formData.name,
      description: formData.description,
      category: formData.category,
      purchaseDate: formData.purchaseDate,
      purchasePrice: parseFloat(formData.purchasePrice),
      usefulLife: parseInt(formData.usefulLife),
      depreciationMethod: formData.depreciationMethod,
      supplier: formData.supplier,
      invoiceNumber: formData.invoiceNumber,
      location: formData.location,
      automatId: formData.automatId || undefined,
      automatName: selectedAutomat?.name || undefined,
      status: 'aktiv' as const,
      notes: formData.notes,
      monthlyRevenue: formData.monthlyRevenue ? parseFloat(formData.monthlyRevenue) : undefined
    };

    const calculatedValues = calculateDepreciation(baseInvestment);
    
    const investmentData = {
      ...baseInvestment,
      ...calculatedValues
    };

    try {
      if (editingInvestment) {
        // Update existing investment
        const { error } = await supabase
          .from('daten')
          .update({
            data: investmentData
          })
          .eq('id', editingInvestment.id);

        if (error) throw error;

        setInvestments(prev => prev.map(inv => 
          inv.id === editingInvestment.id 
            ? { id: editingInvestment.id, ...investmentData }
            : inv
        ));

        setIsEditModalOpen(false);
        setEditingInvestment(null);
      } else {
        // Create new investment
        const { data, error } = await supabase
          .from('daten')
          .insert({
            user_id: user?.id,
            data_type: 'investment',
            data: investmentData
          })
          .select()
          .single();

        if (error) throw error;

        setInvestments(prev => [{
          id: data.id,
          ...investmentData
        }, ...prev]);

        setIsModalOpen(false);
      }

      resetForm();
    } catch (error) {
      console.error('Error saving investment:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Investition wirklich löschen?')) return;

    try {
      const { error } = await supabase
        .from('daten')
        .delete()
        .eq('id', id);

      if (error) throw error;

      setInvestments(prev => prev.filter(investment => investment.id !== id));
    } catch (error) {
      console.error('Error deleting investment:', error);
    }
  };

  const getStatusInfo = (status: string) => {
    return statuses.find(s => s.value === status) || statuses[0];
  };

  const filteredInvestments = investments.filter(investment => {
    const matchesCategory = !filterCategory || investment.category === filterCategory;
    const matchesStatus = !filterStatus || investment.status === filterStatus;
    const matchesSearch = !searchTerm || 
      investment.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      investment.supplier.toLowerCase().includes(searchTerm.toLowerCase()) ||
      investment.invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesCategory && matchesStatus && matchesSearch;
  });

  // Statistiken
  const totalInvestmentValue = investments.reduce((sum, inv) => sum + inv.purchasePrice, 0);
  const totalCurrentValue = investments.reduce((sum, inv) => sum + inv.currentValue, 0);
  const totalAnnualDepreciation = investments.filter(inv => inv.status === 'aktiv').reduce((sum, inv) => sum + inv.annualDepreciation, 0);
  const totalMonthlyRevenue = investments.filter(inv => inv.monthlyRevenue).reduce((sum, inv) => sum + (inv.monthlyRevenue || 0), 0);
  const averageROI = investments.filter(inv => inv.roi).length > 0 
    ? investments.filter(inv => inv.roi).reduce((sum, inv) => sum + (inv.roi || 0), 0) / investments.filter(inv => inv.roi).length
    : 0;

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-line text-4xl text-blue-600 animate-spin mb-4"></i>
          <p className="text-gray-600">Lade Investitionen...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full overflow-x-hidden">
      <div className="w-full max-w-full overflow-x-hidden">
        <div className="bg-white rounded-lg shadow-sm border w-full overflow-x-hidden">
          <div className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200">
            <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:justify-between sm:items-center">
              <div className="min-w-0 flex-1">
                <h1 className="text-base sm:text-xl lg:text-2xl font-bold text-gray-900 flex items-center">
                  Investitionen & Abschreibungen
                  <span className="ml-2 sm:ml-3 text-xs sm:text-sm text-blue-600">
                    <i className="ri-cloud-line mr-1"></i>
                    Supabase
                  </span>
                </h1>
                <p className="text-gray-600 mt-1 text-xs sm:text-base break-words">
                  Verwaltung Ihrer Investitionen mit Abschreibungsplan und ROI-Analyse ({investments.length} Investitionen)
                </p>
              </div>
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full sm:w-auto bg-blue-600 text-white px-3 sm:px-4 py-2 rounded-md hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center flex-shrink-0 text-xs sm:text-sm"
              >
                <i className="ri-add-line w-3 h-3 sm:w-4 sm:h-4 flex items-center justify-center mr-1 sm:mr-2"></i>
                Neue Investition
              </button>
            </div>
          </div>

          {/* Erweiterte Statistik Dashboard */}
          {investments.length > 0 && (
            <div className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Gesamtinvestitionen</p>
                      <p className="text-lg sm:text-xl font-bold text-gray-900">CHF {totalInvestmentValue.toLocaleString()}</p>
                    </div>
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <i className="ri-funds-line text-blue-600 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Aktueller Wert</p>
                      <p className="text-lg sm:text-xl font-bold text-green-600">CHF {totalCurrentValue.toLocaleString()}</p>
                    </div>
                    <div className="p-2 bg-green-100 rounded-lg">
                      <i className="ri-line-chart-line text-green-600 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Jährl. Abschreibung</p>
                      <p className="text-lg sm:text-xl font-bold text-orange-600">CHF {totalAnnualDepreciation.toLocaleString()}</p>
                    </div>
                    <div className="p-2 bg-orange-100 rounded-lg">
                      <i className="ri-arrow-down-s-line text-orange-600 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Monatl. Einnahmen</p>
                      <p className="text-lg sm:text-xl font-bold text-purple-600">CHF {totalMonthlyRevenue.toLocaleString()}</p>
                    </div>
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <i className="ri-money-dollar-circle-line text-purple-600 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                  </div>
                </div>

                <div className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs sm:text-sm text-gray-600">Durchschn. ROI</p>
                      <p className="text-lg sm:text-xl font-bold text-indigo-600">{averageROI.toFixed(1)}%</p>
                    </div>
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <i className="ri-trophy-line text-indigo-600 w-5 h-5 flex items-center justify-center"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Filter und Suche */}
          {investments.length > 0 && (
            <div className="px-2 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
                <div className="flex-1">
                  <div className="relative">
                    <i className="ri-search-line absolute left-2 sm:left-3 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
                    <input
                      type="text"
                      placeholder="Investitionen durchsuchen..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-8 sm:pl-10 pr-3 sm:pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm"
                    />
                  </div>
                </div>
                <div className="flex gap-2 sm:gap-3">
                  <select
                    value={filterCategory}
                    onChange={(e) => setFilterCategory(e.target.value)}
                    className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm pr-6 sm:pr-8"
                  >
                    <option value="">Alle Kategorien</option>
                    {categories.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-2 sm:px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-xs sm:text-sm pr-6 sm:pr-8"
                  >
                    <option value="">Alle Status</option>
                    {statuses.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="p-2 sm:p-4 lg:p-6 w-full overflow-x-hidden">
            {investments.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <div className="text-gray-400 mb-3 sm:mb-4">
                  <i className="ri-funds-line text-4xl sm:text-6xl w-12 h-12 sm:w-16 sm:h-16 flex items-center justify-center mx-auto"></i>
                </div>
                <h3 className="text-base sm:text-lg font-semibold text-gray-700 mb-2">
                  Keine Investitionen vorhanden
                </h3>
                <p className="text-gray-500 text-sm sm:text-base">
                  Erfassen Sie Ihre erste Investition für die Abschreibung und ROI-Analyse.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-6">
                {filteredInvestments.map((investment) => {
                  const statusInfo = getStatusInfo(investment.status);
                  const depreciationPercentage = investment.purchasePrice > 0 ? 
                    (investment.totalDepreciation / investment.purchasePrice) * 100 : 0;
                  
                  return (
                    <div key={investment.id} className="bg-white rounded-lg shadow-sm border overflow-hidden hover:shadow-md transition-shadow">
                      <div className="p-3 sm:p-6">
                        <div className="flex items-start justify-between mb-3 sm:mb-4">
                          <div className="flex-1 min-w-0">
                            <h3 className="text-sm sm:text-lg font-semibold text-gray-900 truncate">{investment.name}</h3>
                            <p className="text-xs sm:text-sm text-gray-500">{investment.category}</p>
                          </div>
                          <span className={`px-2 py-1 text-xs font-medium rounded-full ${statusInfo.color}`}>
                            {statusInfo.label}
                          </span>
                        </div>
                        
                        <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4 line-clamp-2">
                          {investment.description}
                        </p>
                        
                        <div className="space-y-2 mb-3 sm:mb-4">
                          <div className="flex items-center justify-between text-xs sm:text-sm">
                            <span className="text-gray-600">Anschaffung:</span>
                            <span className="font-medium">CHF {investment.purchasePrice.toLocaleString()}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs sm:text-sm">
                            <span className="text-gray-600">Aktueller Wert:</span>
                            <span className="font-medium text-green-600">CHF {investment.currentValue.toLocaleString()}</span>
                          </div>
                          <div className="flex items-center justify-between text-xs sm:text-sm">
                            <span className="text-gray-600">Jährl. Abschreibung:</span>
                            <span className="font-medium text-orange-600">CHF {investment.annualDepreciation.toLocaleString()}</span>
                          </div>
                          {investment.monthlyRevenue && (
                            <div className="flex items-center justify-between text-xs sm:text-sm">
                              <span className="text-gray-600">Monatl. Einnahmen:</span>
                              <span className="font-medium text-purple-600">CHF {investment.monthlyRevenue.toLocaleString()}</span>
                            </div>
                          )}
                          {investment.roi && (
                            <div className="flex items-center justify-between text-xs sm:text-sm">
                              <span className="text-gray-600">ROI:</span>
                              <span className="font-medium text-indigo-600">{investment.roi.toFixed(1)}%</span>
                            </div>
                          )}
                        </div>
                        
                        <div className="mb-3 sm:mb-4">
                          <div className="flex items-center justify-between text-xs sm:text-sm mb-1">
                            <span className="text-gray-600">Abschreibung:</span>
                            <span className="font-medium">{depreciationPercentage.toFixed(1)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                            <div 
                              className="h-1.5 sm:h-2 rounded-full bg-orange-500"
                              style={{ width: `${Math.min(100, depreciationPercentage)}%` }}
                            ></div>
                          </div>
                        </div>
                        
                        <div className="space-y-1 mb-3 sm:mb-4 text-xs sm:text-sm">
                          <div className="flex items-center text-gray-600">
                            <i className="ri-calendar-line mr-1"></i>
                            {new Date(investment.purchaseDate).toLocaleDateString('de-DE')}
                          </div>
                          {investment.supplier && (
                            <div className="flex items-center text-gray-600 truncate">
                              <i className="ri-truck-line mr-1"></i>
                              {investment.supplier}
                            </div>
                          )}
                          {investment.automatName && (
                            <div className="flex items-center text-gray-600 truncate">
                              <i className="ri-robot-line mr-1"></i>
                              {investment.automatName}
                            </div>
                          )}
                        </div>
                        
                        <div className="mt-3 sm:mt-4 flex space-x-1 sm:space-x-2">
                          <Button 
                            size="sm" 
                            variant="secondary" 
                            className="flex-1 text-xs"
                            onClick={() => {
                              setSelectedInvestment(investment);
                              setShowDepreciationModal(true);
                            }}
                          >
                            <i className="ri-line-chart-line mr-1"></i>
                            Plan
                          </Button>
                          {investment.monthlyRevenue && (
                            <Button 
                              size="sm" 
                              variant="secondary" 
                              className="text-xs px-2 sm:px-3"
                              onClick={() => {
                                setSelectedInvestment(investment);
                                setShowROIModal(true);
                              }}
                            >
                              <i className="ri-trophy-line"></i>
                            </Button>
                          )}
                          <Button 
                            size="sm" 
                            variant="secondary" 
                            className="text-xs px-2 sm:px-3"
                            onClick={() => handleEdit(investment)}
                          >
                            <i className="ri-edit-line"></i>
                          </Button>
                          <Button 
                            size="sm" 
                            variant="secondary" 
                            className="text-xs px-2 sm:px-3"
                            onClick={() => handleDelete(investment.id)}
                          >
                            <i className="ri-delete-bin-line"></i>
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal für neue Investition */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          resetForm();
        }}
        title="Neue Investition erfassen"
        maxWidth="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Name der Investition"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="z.B. Automat Modell XY"
              required
            />
            
            <Select
              label="Kategorie"
              options={categories.map(cat => ({ value: cat, label: cat }))}
              value={formData.category}
              onChange={(value) => handleInputChange('category', value)}
              required
            />
            
            <Input
              label="Anschaffungsdatum"
              type="date"
              value={formData.purchaseDate}
              onChange={(e) => handleInputChange('purchaseDate', e.target.value)}
              required
            />
            
            <Input
              label="Anschaffungspreis (CHF)"
              type="number"
              step="0.01"
              value={formData.purchasePrice}
              onChange={(e) => handleInputChange('purchasePrice', e.target.value)}
              placeholder="0.00"
              required
            />
            
            <Input
              label="Nutzungsdauer (Jahre)"
              type="number"
              value={formData.usefulLife}
              onChange={(e) => handleInputChange('usefulLife', e.target.value)}
              placeholder="5"
              required
            />
            
            <Select
              label="Abschreibungsmethode"
              options={depreciationMethods}
              value={formData.depreciationMethod}
              onChange={(value) => handleInputChange('depreciationMethod', value)}
              required
            />
            
            <Input
              label="Monatliche Einnahmen (CHF)"
              type="number"
              step="0.01"
              value={formData.monthlyRevenue}
              onChange={(e) => handleInputChange('monthlyRevenue', e.target.value)}
              placeholder="0.00"
            />
            
            <Input
              label="Lieferant"
              value={formData.supplier}
              onChange={(e) => handleInputChange('supplier', e.target.value)}
              placeholder="Lieferantenname"
            />
            
            <Input
              label="Rechnungsnummer"
              value={formData.invoiceNumber}
              onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
              placeholder="RE-2024-001"
            />
            
            <Input
              label="Standort"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder="Büro, Lager, etc."
            />
            
            <Select
              label="Zugehöriger Automat (optional)"
              options={[
                { value: '', label: 'Kein Automat' },
                ...automaten.map(automat => ({ 
                  value: automat.id, 
                  label: `${automat.name} - ${automat.location}`
                }))
              ]}
              value={formData.automatId}
              onChange={(value) => handleInputChange('automatId', value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Beschreibung</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Detaillierte Beschreibung der Investition..."
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notizen</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="Zusätzliche Informationen..."
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <Button type="submit" className="flex-1">
              Investition erfassen
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setIsModalOpen(false);
                resetForm();
              }}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal für Investition bearbeiten */}
      <Modal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingInvestment(null);
          resetForm();
        }}
        title="Investition bearbeiten"
        maxWidth="xl"
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Name der Investition"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="z.B. Automat Modell XY"
              required
            />
            
            <Select
              label="Kategorie"
              options={categories.map(cat => ({ value: cat, label: cat }))}
              value={formData.category}
              onChange={(value) => handleInputChange('category', value)}
              required
            />
            
            <Input
              label="Anschaffungsdatum"
              type="date"
              value={formData.purchaseDate}
              onChange={(e) => handleInputChange('purchaseDate', e.target.value)}
              required
            />
            
            <Input
              label="Anschaffungspreis (CHF)"
              type="number"
              step="0.01"
              value={formData.purchasePrice}
              onChange={(e) => handleInputChange('purchasePrice', e.target.value)}
              placeholder="0.00"
              required
            />
            
            <Input
              label="Nutzungsdauer (Jahre)"
              type="number"
              value={formData.usefulLife}
              onChange={(e) => handleInputChange('usefulLife', e.target.value)}
              placeholder="5"
              required
            />
            
            <Select
              label="Abschreibungsmethode"
              options={depreciationMethods}
              value={formData.depreciationMethod}
              onChange={(value) => handleInputChange('depreciationMethod', value)}
              required
            />
            
            <Input
              label="Monatliche Einnahmen (CHF)"
              type="number"
              step="0.01"
              value={formData.monthlyRevenue}
              onChange={(e) => handleInputChange('monthlyRevenue', e.target.value)}
              placeholder="0.00"
            />
            
            <Input
              label="Lieferant"
              value={formData.supplier}
              onChange={(e) => handleInputChange('supplier', e.target.value)}
              placeholder="Lieferantenname"
            />
            
            <Input
              label="Rechnungsnummer"
              value={formData.invoiceNumber}
              onChange={(e) => handleInputChange('invoiceNumber', e.target.value)}
              placeholder="RE-2024-001"
            />
            
            <Input
              label="Standort"
              value={formData.location}
              onChange={(e) => handleInputChange('location', e.target.value)}
              placeholder="Büro, Lager, etc."
            />
            
            <Select
              label="Zugehöriger Automat (optional)"
              options={[
                { value: '', label: 'Kein Automat' },
                ...automaten.map(automat => ({ 
                  value: automat.id, 
                  label: `${automat.name} - ${automat.location}`
                }))
              ]}
              value={formData.automatId}
              onChange={(value) => handleInputChange('automatId', value)}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Beschreibung</label>
            <textarea
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={3}
              placeholder="Detaillierte Beschreibung der Investition..."
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notizen</label>
            <textarea
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              rows={2}
              placeholder="Zusätzliche Informationen..."
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <Button type="submit" className="flex-1">
              Änderungen speichern
            </Button>
            <Button 
              type="button" 
              variant="secondary" 
              onClick={() => {
                setIsEditModalOpen(false);
                setEditingInvestment(null);
                resetForm();
              }}
            >
              Abbrechen
            </Button>
          </div>
        </form>
      </Modal>

      {/* Modal für Abschreibungsplan */}
      <Modal
        isOpen={showDepreciationModal}
        onClose={() => setShowDepreciationModal(false)}
        title="Abschreibungsplan"
        maxWidth="lg"
      >
        {selectedInvestment && (
          <div className="space-y-4">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-2">{selectedInvestment.name}</h3>
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Anschaffungspreis:</span>
                  <p className="font-medium">CHF {selectedInvestment.purchasePrice.toLocaleString()}</p>
                </div>
                <div>
                  <span className="text-gray-600">Methode:</span>
                  <p className="font-medium">
                    {depreciationMethods.find(m => m.value === selectedInvestment.depreciationMethod)?.label}
                  </p>
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full border-collapse border border-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-900">Jahr</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-900">Anfangswert</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-900">Abschreibung</th>
                    <th className="border border-gray-300 px-4 py-2 text-left text-sm font-medium text-gray-900">Endwert</th>
                  </tr>
                </thead>
                <tbody>
                  {generateDepreciationSchedule(selectedInvestment).map((entry) => (
                    <tr key={entry.year} className="hover:bg-gray-50">
                      <td className="border border-gray-300 px-4 py-2 text-sm">{entry.year}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm">CHF {entry.startValue.toLocaleString()}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm text-red-600">CHF {entry.depreciation.toLocaleString()}</td>
                      <td className="border border-gray-300 px-4 py-2 text-sm font-medium">CHF {entry.endValue.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </Modal>

      {/* Modal für ROI-Analyse */}
      <Modal
        isOpen={showROIModal}
        onClose={() => setShowROIModal(false)}
        title="ROI-Analyse"
        maxWidth="lg"
      >
        {selectedInvestment && selectedInvestment.monthlyRevenue && (
          <div className="space-y-6">
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-6 rounded-lg">
              <h3 className="font-semibold text-gray-900 mb-4">{selectedInvestment.name}</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Investitionssumme</span>
                      <span className="font-bold text-lg">CHF {selectedInvestment.purchasePrice.toLocaleString()}</span>
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Monatliche Einnahmen</span>
                      <span className="font-bold text-lg text-green-600">CHF {selectedInvestment.monthlyRevenue.toLocaleString()}</span>
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Jährliche Einnahmen</span>
                      <span className="font-bold text-lg text-green-600">CHF {(selectedInvestment.monthlyRevenue * 12).toLocaleString()}</span>
                    </div>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">ROI (Return on Investment)</span>
                      <span className="font-bold text-2xl text-indigo-600">{selectedInvestment.roi?.toFixed(1)}%</span>
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Amortisationsdauer</span>
                      <span className="font-bold text-lg text-purple-600">
                        {selectedInvestment.paybackPeriod && selectedInvestment.paybackPeriod < 12 
                          ? `${selectedInvestment.paybackPeriod.toFixed(1)} Monate`
                          : `${(selectedInvestment.paybackPeriod! / 12).toFixed(1)} Jahre`
                        }
                      </span>
                    </div>
                  </div>
                  
                  <div className="bg-white p-4 rounded-lg shadow-sm">
                    <div className="flex items-center justify-between">
                      <span className="text-gray-600">Status</span>
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                        selectedInvestment.roi && selectedInvestment.roi > 15 
                          ? 'bg-green-100 text-green-800' 
                          : selectedInvestment.roi && selectedInvestment.roi > 8
                          ? 'bg-yellow-100 text-yellow-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {selectedInvestment.roi && selectedInvestment.roi > 15 
                          ? 'Sehr rentabel' 
                          : selectedInvestment.roi && selectedInvestment.roi > 8
                          ? 'Rentabel'
                          : 'Wenig rentabel'
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="mt-6 p-4 bg-white rounded-lg">
                <h4 className="font-medium text-gray-900 mb-3">Prognose für die nächsten 5 Jahre</h4>
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map(year => {
                    const totalRevenue = selectedInvestment.monthlyRevenue! * 12 * year;
                    const profit = totalRevenue - selectedInvestment.purchasePrice;
                    return (
                      <div key={year} className="flex justify-between items-center text-sm">
                        <span className="text-gray-600">Jahr {year}:</span>
                        <span className={`font-medium ${profit > 0 ? 'text-green-600' : 'text-red-600'}`}>
                          CHF {totalRevenue.toLocaleString()} 
                          ({profit > 0 ? '+' : ''}{profit.toLocaleString()} Gewinn)
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
